import { ActionsFooter } from "@/Component/ActionsFooter";
import { Button, Modal, Stack } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { Spinner } from "reactstrap";
import { useLocalizedText } from "@/utils/hooks/useLocalizedText";
import { SelectCountry } from "@/Component/Form/SelectCountry";
import TableC from "@/CommonElements/TableC";
import { useEffect, useState } from "react";
import { Alert, Checkbox, Divider, Flex, Tag } from "antd";
import { Controllers } from "@/CommonElements/Controller";
import { cn, handleFormErrors } from "@/utils/helpers";
import { AntInput } from "@/CommonElements/AntInput";
import { SelectCachedOptions } from "@/Component/SelectCachedOptions";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import { Cancel01Icon, DeleteIcon, HajiIcon } from "@hugeicons/core-free-icons/index";
import { HugeiconsIcon } from "@hugeicons/react";
import BtnAsLink from "@/Component/BtnAsLink";
import { validateArabicOnly, validateEnglishOnly } from "@/Pages/ProfileForm";
import { AntSelect } from "@/CommonElements/AntSelect";
import { useAccomodate } from "./ReceptionCenter/hooks/useAccomodate";
import { useSearchQuery } from "@/utils/hooks/useSearchQuery";
import { Link } from "react-router";

export const AccommodatePilgrimsModal = ({ show, onClose, arrival }) => {
    const [page, setPage] = useState(1);
    const [pageSize] = useState(10);
    const { t } = useTranslation();
    const { getLocalizedText } = useLocalizedText();
    const [selectedPilgrims, setSelectedPilgrims] = useState([]);

    // ============================================
    // Side Effects
    // ============================================
    const { accomodate, cancelAccommodate, isPending, meta, pilgrims, loading } = useAccomodate({
        page,
        pageSize,
        manifestId: arrival?.manifest_id || arrival?.tdm_id,
    });
    const form = useForm();
    const [searchParams] = useSearchQuery();
    const isBetweenCities = searchParams?.source === "between_cities"
    const { control, formState: { errors } } = form;
    const manualPilgrims = form.watch("manual");

    const { fields, append, remove } = useFieldArray({
        control,
        name: "manual",
    });

    useEffect(() => {
        form.reset({
            service_center_id: arrival?.accommodation?.service_center_id,
            cabinet_number: arrival?.accommodation?.cabinet_number,
            contract_id: arrival?.accommodation?.contract_id
        });
    }, [arrival]);

    const handleSelectAllPilgrims = (checked) => {
        const selectedIds = pilgrims?.filter((p) => !p.is_accommodated).map((p) => ({ id: p.id, passport_type: "actual_passport" })).filter((id) => !selectedPilgrims.some(p => p.id === id));
        setSelectedPilgrims(prev => checked ? [...prev, ...selectedIds] : []);
    };



    const notAccommodatedPilgrims = pilgrims?.filter((p) => !p.is_accommodated)

    const isAllSelected = notAccommodatedPilgrims?.every((p) => selectedPilgrims.some(p => p.id === p.id)) && !!notAccommodatedPilgrims?.length
    const isSomeSelected = !isAllSelected && pilgrims?.some((p) => selectedPilgrims.some(p => p.id === p.id));
    const columns = [
        {
            name: <Checkbox disabled={loading || isPending} checked={!loading && isAllSelected} indeterminate={!loading && isSomeSelected} className="square-checkbox" onChange={(e) => handleSelectAllPilgrims(e.target.checked)} />,
            key: "id",
            render: (value, row) => <Checkbox
                className="square-checkbox"
                disabled={row.is_accommodated || loading || isPending}
                checked={selectedPilgrims.some(p => p.id === value)}
                value={value} onChange={(e) => {
                    if (e.target.checked) {
                        setSelectedPilgrims(prev => [...prev, { id: value, passport_type: "actual_passport" }]);
                    } else {
                        setSelectedPilgrims(prev => prev.filter((p) => p.id !== value));
                    }
                }} />,
        },
        {
            name: t("FULL_NAME"),
            key: "full_name_ar",
            render: (value, row) => <Link to={`/app/madina/pilgrims/${row.id}/view`}>{getLocalizedText(value, row.full_name_en) || "-"}</Link>,
        },
        {
            name: t("NATIONALITY"),
            key: "current_nationality_id",
            render: (value) => <SelectCountry.Display value={value} />
        },
        {
            name: t("HAJJ_ID"),
            key: "applicant_id",
            render: (value) => value || "-",
        },
        {
            name: t("PASSPORT_NUMBER"),
            key: "passport_number",
            render: (value) => value || "-",
        },
        {
            name: t("PASSPORT_TYPE"),
            key: "passport_type",
            render: (value, row) => {
                const selectedPilgrim = selectedPilgrims.find(p => p.id === row.id);
                const selectedValue = selectedPilgrim?.passport_type || value || "actual_passport";

                return <AntSelect
                    options={[
                        {
                            label: t("ACTUAL_PASSPORT"),
                            value: "actual_passport",
                        },
                        {
                            label: t("PASSPORT_IMAGE"),
                            value: "passport_image",
                        },
                        {
                            label: t("WITHOUT_PASSPORT"),
                            value: "without_passport",
                        },
                    ]}
                    disabled={!selectedPilgrim || loading || isPending}
                    value={selectedValue}
                    onChange={(value) => {
                        setSelectedPilgrims(prev => {
                            return prev.map(p => String(p.id) === String(row.id) ? ({ ...p, passport_type: value }) : p)
                        });
                    }}
                />
            }
        },
        {
            name: t("ACCOMMODATION_STATUS"),
            key: "is_accommodated",
            render: (value) => <Tag color={value ? "green" : "red"}>{value ? t("ACCOMMODATED") : t("NOT_ACCOMMODATED")}</Tag>,
        },
        isBetweenCities && {
            name: t("AD_GROUP_NAME"),
            key: "adGroupNameAr",
        },
        {
            name: t("ACTIONS"),
            key: "actions",
        },
    ].filter(Boolean);


    const onConfirm = (data) => {
        const payload = {
            ...data,
            pilgrims: selectedPilgrims,
        };



        accomodate({ manifest_id: arrival?.manifest_id || arrival?.tdm_id, ...payload }, {
            onSuccess: () => {
                setSelectedPilgrims([]);
            },
            onError: (error) => {
                handleFormErrors(error, form.setError, t);
            }
        });
    };

    const ctrls = [
        {
            labelProps: { children: t("SERVICE_CENTER"), className: "required" },
            controllerProps: {
                name: "service_center_id",
                rules: { required: t("FIELD_REQUIRED") },
                render: ({ field, fieldState: { invalid } }) => (
                    <SelectCachedOptions
                        {...field}
                        showSearch
                        id={field.name}
                        placeholder={t("SERVICE_CENTER")}
                        status={invalid ? "error" : undefined}
                        optionKey="service_centers"
                        filter={(m) => m.system === "madina"}
                        options={(m) => ({
                            value: m.id,
                            label: m.name,
                        })}
                    />
                ),
            },
        },
        {
            labelProps: { children: t("CABINET_NUMBER"), className: "required" },
            controllerProps: {
                name: "cabinet_number",
                rules: { required: t("FIELD_REQUIRED") },
                render: ({ field, fieldState: { invalid } }) => (
                    <AntInput
                        {...field}
                        id={field.name}
                        placeholder={t("CABINET_NUMBER")}
                        status={invalid ? "error" : undefined}
                        type="number"
                    />
                ),
            },
        },
        {
            labelProps: { children: t("CONTRACT_ID"), className: "required" },
            controllerProps: {
                name: "contract_id",
                rules: { required: t("FIELD_REQUIRED") },
                render: ({ field, fieldState: { invalid } }) => (
                    <SelectCachedOptions
                        {...field}
                        id={field.name}
                        placeholder={t("CONTRACT_ID")}
                        status={invalid ? "error" : undefined}
                        optionKey="madinahContracts"
                        showSearch
                        options={(m) => ({
                            value: m.id,
                            label: [m.id, m.house_commercial_name_ar].join(" - "),
                        })}
                    />
                ),
            },
        },
    ];


    const manualPilgrimsColumns = [
        {
            name: t("PASSPORT_NUMBER"),
            key: "full_name_ar",
            render: (value, row, index) => <Controller
                rules={{ required: t("FIELD_REQUIRED") }}
                name={`manual.${index}.passport_number`}
                control={control}
                render={({ field, fieldState: { invalid, error } }) => (
                    <Stack>
                        <AntInput
                            {...field}
                            status={invalid ? "error" : undefined}
                            id={field.name}
                            placeholder={t("PASSPORT_NUMBER")}
                        />
                        {error && <Alert message={error.message} type="error" />}
                    </Stack>
                )}
            />
        },
        {
            name: t("NATIONALITY"),
            key: "full_name_ar",
            render: (value, row, index) => <Controller
                rules={{ required: t("FIELD_REQUIRED") }}
                name={`manual.${index}.nationality_id`}
                control={control}
                render={({ field, fieldState: { invalid, error } }) => (
                    <Stack>
                        <SelectCountry
                            {...field}
                            status={invalid ? "error" : undefined}
                            style={{ minWidth: "180px", height: "45px" }}
                            id={field.name}
                            placeholder={t("NATIONALITY")}
                        />
                        {error && <Alert message={error.message} type="error" />}
                    </Stack>
                )}
            />
        },
        {
            name: t("FULL_NAME_AR"),
            key: "full_name_ar",
            render: (value, row, index) => <Controller
                rules={{
                    validate: (value) => validateArabicOnly(value, t)
                }}
                name={`manual.${index}.pilgrim_name_ar`}
                control={control}
                render={({ field, fieldState: { invalid, error } }) => (
                    <Stack>
                        <AntInput
                            {...field}
                            status={invalid ? "error" : undefined}
                            id={field.name}
                            placeholder={t("FULL_NAME_AR")}
                        />
                        {error && <Alert message={error.message} type="error" />}
                    </Stack>
                )}
            />
        },
        {
            name: t("FULL_NAME_EN"),
            key: "full_name_ar",
            render: (value, row, index) => <Controller
                rules={{
                    validate: (value) => validateEnglishOnly(value, t)
                }}
                name={`manual.${index}.pilgrim_name_en`}
                control={control}
                render={({ field, fieldState: { invalid, error } }) => (
                    <Stack>
                        <AntInput
                            {...field}
                            status={invalid ? "error" : undefined}
                            id={field.name}
                            placeholder={t("FULL_NAME_EN")}
                        />
                        {error && <Alert message={error.message} type="error" />}
                    </Stack>
                )}
            />
        },
        {
            name: t("PASSPORT_TYPE"),
            key: "full_name_ar", // TODO: add passport type
            render: (value, row, index) => <Controller
                name={`manual.${index}.passport_type`}
                control={control}
                render={({ field, fieldState: { invalid, error } }) => (
                    <Stack>
                        <AntSelect
                            {...field}
                            status={invalid ? "error" : undefined}
                            id={field.name}
                            placeholder={t("PASSPORT_TYPE")}
                            options={[
                                {
                                    label: t("ACTUAL_PASSPORT"),
                                    value: "actual_passport",
                                },
                                {
                                    label: t("PASSPORT_IMAGE"),
                                    value: "passport_image",
                                },
                                {
                                    label: t("WITHOUT_PASSPORT"),
                                    value: "without_passport",
                                },
                            ]}
                        />
                        {error && <Alert message={error.message} type="error" />}
                    </Stack>
                )}
            />
        },
        {
            name: t("ACTIONS"),
            key: "actionss",
            render: (value, row, index) => <BtnAsLink type="button" className="btn btn-danger btn-sm btn-icon" icon={<HugeiconsIcon size={16} icon={DeleteIcon} />} onClick={() => remove(index)} />
        },
    ];


    const getActions = (rowData) => {
        if (!rowData.is_accommodated) {

            return [{
                key: "accommodate",
                label: <BtnAsLink
                    disabled={isPending || selectedPilgrims.length > 1}
                    icon={<HugeiconsIcon size={16} icon={HajiIcon} />}
                    type="submit"
                    className="btn btn-primary btn-sm">
                    {t("CAMPAIGN_SETTLEMENT")}
                </BtnAsLink>

            }]
        }

        return [{
            key: "unaccommodate",
            label: <BtnAsLink
                disabled={isPending}
                onClick={() => cancelAccommodate({ manifest_id: arrival?.manifest_id || arrival?.tdm_id, passport_number: rowData.passport_number })}
                icon={
                    <HugeiconsIcon size={16} icon={Cancel01Icon} />
                }
                type="button" className="btn btn-danger btn-sm">
                {t("CANCEL")}
            </BtnAsLink>,
        }];
    };


    return (
        <Modal
            scrollable show={show} onHide={onClose} centered size="xl"
            backdrop="static"
            keyboard={false}>
            <Modal.Header closeButton>
                <Modal.Title>{t("ACCOMMODATE_PILGRIMS")}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <form onSubmit={form.handleSubmit(onConfirm)}>
                    <div>
                        <Controllers
                            isLoading={loading}
                            watch={form.watch}
                            {...{
                                controllers: ctrls.map((config) => ({
                                    ...config,
                                    labelProps: {
                                        ...config.labelProps,
                                        className: cn("fw-bold required", config.labelProps.className),
                                    },
                                    controllerProps: {
                                        ...config.controllerProps,
                                        rules: {
                                            ...(config.controllerProps.rules || {}),
                                        },
                                        control,
                                        errors,
                                    },
                                    layout: {
                                        colProps: { xs: 6, md: 6 },
                                    },
                                })),
                            }}
                        />
                    </div>
                    <div className="my-3 d-flex justify-content-between align-items-center">
                        <p className="fw-bold">{t("NEW_PILGRIM_DETAILS")}</p>
                        <button
                            disabled={loading || isPending}
                            type="button"
                            className="btn btn-outline-primary btn-sm"
                            onClick={() => append({
                                passport_number: "",
                                nationality_id: "",
                                pilgrim_name_ar: "",
                                pilgrim_name_en: "",
                            })}>
                            {t("MANUAL_PILGRIMS")}
                        </button>

                    </div>

                    <Controller
                        name="manual" control={control} render={({ field, fieldState: { invalid, error } }) => (
                            <>
                                <div>
                                    {error && <Alert message={error?.root?.message || error?.message} type="error" />}
                                </div>
                                {!!fields.length && (
                                    <div style={{
                                        border: "1px dashed #000",
                                        padding: "10px",
                                        borderRadius: "10px",
                                        overflow: "hidden",
                                    }}>
                                        <TableC
                                            columns={manualPilgrimsColumns}
                                            data={fields}
                                            columnsFilter={false}
                                        />
                                    </div>
                                )}
                            </>
                        )} />

                    <Divider />
                    <div style={{
                        marginBottom: "63px"
                    }}>
                        <TableC
                            columnsFilter={false}
                            columns={columns}
                            data={pilgrims}
                            loading={loading}
                            actions={getActions}
                            pagination={{
                                pageSize: pageSize,
                                current: page,
                                total: meta?.total || 0,
                                onChange: (page) => {
                                    setPage(page);
                                }
                            }}
                        />
                    </div>


                    <ActionsFooter>
                        {(selectedPilgrims.length > 1 || manualPilgrims?.length > 0) && (
                            <Button disabled={(!selectedPilgrims.length && !manualPilgrims?.length) || (!selectedPilgrims.length && !manualPilgrims?.length) || isPending || loading} type="submit" className="btn-primary btn-sm">
                                {isPending && <Spinner size="sm" />}
                                {t("CONFIRM")}
                            </Button>
                        )}

                        <Button disabled={isPending} style={{ pointerEvents: isPending ? 'none' : 'auto' }} type="button" onClick={onClose} className="btn-danger btn-sm">
                            {t("CANCEL")}
                        </Button>
                    </ActionsFooter>
                </form>
            </Modal.Body>
        </Modal >
    );
};
